import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import AccountingToolPage from '@/components/AccountingToolPage'
import { toast } from '@/store/toastStore'
import TxnConflictSearchBar from '@/components/TxnConflictSearchBar'
import TxnSearchFilterBar from '@/components/TxnSearchFilterBar'
import {
  ALL_COLUMN_KEYS,
  DEFAULT_WIDTHS,
  FROZEN_COLUMN_KEYS,
  QBO_TXN_COLUMN_LABELS,
  QBO_TXN_DEFAULT_SORT,
  QBO_TXN_FIELD_DEFINITIONS,
  QBO_TXN_SEARCH_PAGE_KEY,
  buildQboTxnSearchFilterFields,
  fixedColumnStyle,
  isPinnedColumn,
  isQboTxnColumnSortable,
  migrateLegacyColumnWidths,
  pinnedCellClass,
  pinnedHeaderClass,
  type QboTxnColumnKey,
} from '@/pages/qboTransactionSearchTableConfig'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  HoverHelp,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  getVisibleFieldKeys,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type ColumnVisibility,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { useQBOStore } from '@/store/qboStore'
import {
  useQboTransactionSearchStore,
  type QboTxnSearchTabId,
} from '@/store/qboTransactionSearchStore'
import { getJobStatus } from '@/services/jobs'
import {
  getApplyResultsFromJob,
  getCategoryConflictIgnores,
  getConflictGroupsFromJob,
  getConflictStatsFromJob,
  getSearchResultsFromJob,
  getSearchStatsFromJob,
  putCategoryConflictIgnores,
  startQboTransactionApply,
  startQboTransactionSearch,
  type IgnoredConflictDescriptionEntry,
  type IgnoredConflictGroupEntry,
  type QboTransactionSearchCriteria,
  type QboTransactionSearchRow,
} from '@/services/qboTransactionSearch'
import { formatApiError } from '@/utils/apiError'
import { getSettings, updateSetting } from '@/services/settings'
import {
  buildSearchCriteriaRecap,
  criteriaEqual,
  filterQboTxnRowsWithTree,
  filterTransactionRowsByText,
  formatSearchCriteriaSummary,
} from '@/utils/qboTransactionResultFilters'
import {
  buildApplyPayloadFields,
  clearPendingRowEdits,
  displayCategory,
  displayDescription,
  displayDocNumber,
  displayLocation,
  queueCategoryEdit,
  queueDescriptionEdit,
  queueDocNumberEdit,
  queueLocationEdit,
  rowHasPendingChanges,
  supportsHeaderLocationEdit,
} from '@/utils/qboTransactionRowEdits'
import {
  openQboUrls,
  uniqueQboUrlsFromSelectedRows,
} from '@/utils/qboTransactionOpenSelected'
import { toQualifiedSelectOption } from '@/utils/qboQualifiedName'
import {
  bucketRowsByConflictGroup,
  buildCategoryConflictIgnoreSets,
  buildConflictGroupViews,
  buildIgnoredDescriptionEntriesFromRows,
  defaultConflictRowCompare,
  filterIgnoredConflictRows,
  filterResolvedConflictRows,
  formatConflictCategoryChip,
  formatConflictGroupLineCount,
  getTrailingTwelveMonthsDateRange,
  keysInVisibleRange,
  mergeApplyStatusesIntoRows,
  type ConflictGroupView,
} from '@/utils/qboTransactionConflictGroups'

/** Shared app setting — persisted server-side for all users. */
const QBO_TXN_SEARCH_LOCK_DATE_KEY = 'qbo_txn_search_lock_date'

/** True when a transaction is dated strictly before the lock date (books closed through that date). */
function isDateLocked(date: string | undefined, lockDate: string): boolean {
  if (!lockDate || !date) return false
  return date < lockDate
}

function selectOptionsWithValue(
  options: SelectOption[],
  value: string | null | undefined
): SelectOption[] {
  if (!value) return options
  if (options.some((option) => option.id === value)) return options
  return [toQualifiedSelectOption(value), ...options]
}

function LineDescriptionInput({
  row,
  onCommit,
}: {
  row: QboTransactionSearchRow
  onCommit: (patch: Pick<QboTransactionSearchRow, 'line_item_description_new'>) => void
}) {
  const displayValue = displayDescription(row)
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState(displayValue)

  useEffect(() => {
    if (!focused) {
      setDraft(displayValue)
    }
  }, [displayValue, focused])

  return (
    <input
      className="input box-border h-full min-h-9 w-full min-w-0 px-2 text-sm leading-normal"
      value={focused ? draft : displayValue}
      onFocus={() => {
        setDraft(displayValue)
        setFocused(true)
      }}
      onBlur={() => {
        setFocused(false)
        onCommit(queueDescriptionEdit(row, draft))
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
        }
      }}
    />
  )
}

function DocNumberInput({
  row,
  onCommit,
}: {
  row: QboTransactionSearchRow
  onCommit: (patch: Pick<QboTransactionSearchRow, 'doc_number_new'>) => void
}) {
  const displayValue = displayDocNumber(row)
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState(displayValue)

  useEffect(() => {
    if (!focused) {
      setDraft(displayValue)
    }
  }, [displayValue, focused])

  return (
    <input
      className="input box-border h-full min-h-9 w-full min-w-0 px-2 text-sm leading-normal"
      value={focused ? draft : displayValue}
      maxLength={21}
      aria-label="Journal number"
      onFocus={() => {
        setDraft(displayValue)
        setFocused(true)
      }}
      onBlur={() => {
        setFocused(false)
        onCommit(queueDocNumberEdit(row, draft))
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
        }
      }}
    />
  )
}

function TxnSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  style,
  onStartResize,
  alignRight = false,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  style: CSSProperties
  onStartResize: (key: string, event: ReactMouseEvent) => void
  alignRight?: boolean
}) {
  const isSorted = sortDirection !== null

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${alignRight ? 'text-right' : ''} ${className}`}
      style={style}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
    </th>
  )
}

const SEARCH_TABS: { id: QboTxnSearchTabId; label: string }[] = [
  { id: 'basic', label: 'Basic search' },
  { id: 'conflicts', label: 'Inconsistent categories' },
]

type ConflictViewTabId = 'active' | 'ignored'

function SearchTabNav({
  activeTab,
  onSelect,
}: {
  activeTab: QboTxnSearchTabId
  onSelect: (tab: QboTxnSearchTabId) => void
}) {
  return (
    <div
      className="inline-flex min-w-max items-center gap-1 rounded-lg bg-gray-200 p-1"
      role="tablist"
      aria-label="Transaction search mode"
    >
      {SEARCH_TABS.map((tab) => {
        const isActive = activeTab === tab.id
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab.id)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              isActive ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

function rowKey(row: QboTransactionSearchRow): string {
  return `${row.transaction_id}\0${row.line_item_id}`
}

function SearchCriteriaSummary({ criteria }: { criteria: QboTransactionSearchCriteria }) {
  const recap = buildSearchCriteriaRecap(criteria)
  const line = [recap.datesLine, ...recap.inlineItems.map((item) => item.text)]
    .filter(Boolean)
    .join(' · ')
  return (
    <p className="mt-1 truncate text-sm text-gray-600" title={formatSearchCriteriaSummary(criteria)}>
      {line || 'No filters (all transactions in date range)'}
    </p>
  )
}

/** Accounting format: $#,##0.00;($#,##0.00);- */
function formatAmount(value: number | string): string {
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  if (!Number.isFinite(n)) return String(value)
  if (n === 0) return '-'
  const formatted = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return n < 0 ? `($${formatted})` : `$${formatted}`
}

const TXN_READONLY_CELL = 'flex h-full min-h-9 w-full items-center'

export default function QboTransactionSearch() {
  const activeTab = useQboTransactionSearchStore((s) => s.activeTab)
  const tabSlice = useQboTransactionSearchStore((s) =>
    s.activeTab === 'conflicts' ? s.conflicts : s.basic
  )
  const criteria = tabSlice.criteria
  const searchedCriteria = tabSlice.searchedCriteria
  const resultFilterTree = tabSlice.resultFilterTree
  const resultTextSearch = tabSlice.resultTextSearch
  const rows = tabSlice.rows
  const searchStats = tabSlice.searchStats
  const conflictGroups = tabSlice.conflictGroups
  const conflictStats = tabSlice.conflictStats
  const showResults = tabSlice.showResults
  const setActiveTab = useQboTransactionSearchStore((s) => s.setActiveTab)
  const setCriteria = useQboTransactionSearchStore((s) => s.setCriteria)
  const setResultFilterTree = useQboTransactionSearchStore((s) => s.setResultFilterTree)
  const setResultTextSearch = useQboTransactionSearchStore((s) => s.setResultTextSearch)
  const clearResultFilters = useQboTransactionSearchStore((s) => s.clearResultFilters)
  const setSearchResults = useQboTransactionSearchStore((s) => s.setSearchResults)
  const updateRows = useQboTransactionSearchStore((s) => s.updateRows)
  const resetSearch = useQboTransactionSearchStore((s) => s.reset)
  const expireIfStale = useQboTransactionSearchStore((s) => s.expireIfStale)

  const vendors = useQBOStore((s) => s.vendors)
  const locations = useQBOStore((s) => s.locations)
  const categories = useQBOStore((s) => s.categories)

  const [searchJobId, setSearchJobId] = useState<number | null>(null)
  const [applyJobId, setApplyJobId] = useState<number | null>(null)
  const [searching, setSearching] = useState(false)
  const [applying, setApplying] = useState(false)
  const [showApplyConfirm, setShowApplyConfirm] = useState(false)
  const [searchFormExpanded, setSearchFormExpanded] = useState(!showResults)
  const [showResultFilters, setShowResultFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(QBO_TXN_DEFAULT_SORT)
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(QBO_TXN_SEARCH_PAGE_KEY, QBO_TXN_FIELD_DEFINITIONS)
  )
  const [lockDate, setLockDate] = useState('')
  const [lockDateLoading, setLockDateLoading] = useState(true)
  const [lockDateSaving, setLockDateSaving] = useState(false)
  const [lockDateUnlocked, setLockDateUnlocked] = useState(false)
  const [lockOverrideAcknowledged, setLockOverrideAcknowledged] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set())
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [bulkApplyCategory, setBulkApplyCategory] = useState(false)
  const [bulkApplyDescription, setBulkApplyDescription] = useState(false)
  const [bulkApplyLocation, setBulkApplyLocation] = useState(false)
  const [bulkCategoryNew, setBulkCategoryNew] = useState<string | null>(null)
  const [bulkDescriptionNew, setBulkDescriptionNew] = useState('')
  const [bulkLocationNew, setBulkLocationNew] = useState<string | null>(null)
  const [hasUserSorted, setHasUserSorted] = useState(false)
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set())
  const [conflictViewTab, setConflictViewTab] = useState<ConflictViewTabId>('active')
  const [ignoredGroups, setIgnoredGroups] = useState<IgnoredConflictGroupEntry[]>([])
  const [ignoredDescriptions, setIgnoredDescriptions] = useState<IgnoredConflictDescriptionEntry[]>(
    []
  )
  const [ignoresLoading, setIgnoresLoading] = useState(false)
  const [ignoresSaving, setIgnoresSaving] = useState(false)

  const selectionAnchorKeyRef = useRef<string | null>(null)
  const suppressCheckboxChangeForRowKeyRef = useRef<string | null>(null)
  const headerSelectAllCheckboxRef = useRef<HTMLInputElement>(null)
  const applyTabRef = useRef<QboTxnSearchTabId>(activeTab)

  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)
  const serverById = useBackgroundJobsStore((s) => s.serverById)

  const vendorOptions: SelectOption[] = useMemo(
    () =>
      vendors
        .filter((v) => v.active)
        .map((v) => ({ id: v.display_name, label: v.display_name }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [vendors]
  )

  const categoryOptions: SelectOption[] = useMemo(
    () =>
      categories
        .filter((c) => c.active)
        .map((c) => toQualifiedSelectOption(c.name, c.account_type))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [categories]
  )

  const locationOptions: SelectOption[] = useMemo(
    () =>
      locations
        .filter((l) => l.active)
        .map((l) => ({ id: l.name, label: l.name }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [locations]
  )

  const tableCategoryOptions: SelectOption[] = useMemo(
    () => categories.map((c) => toQualifiedSelectOption(c.name, c.account_type)),
    [categories]
  )

  const tableLocationOptions: SelectOption[] = useMemo(
    () => [
      { id: '', label: '(No location)' },
      ...locations.map((l) => ({ id: l.name, label: l.name })),
    ],
    [locations]
  )

  const qboVendorFieldOptions = useMemo(
    () => vendorOptions.map((option) => ({ value: option.label, label: option.label })),
    [vendorOptions]
  )

  const qboCategoryFieldOptions = useMemo(
    () =>
      categories
        .filter((c) => c.active)
        .map((c) => ({ value: c.name, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [categories]
  )

  const qboLocationFieldOptions = useMemo(
    () => locationOptions.map((option) => ({ value: option.label, label: option.label })),
    [locationOptions]
  )

  const qboFilterFields = useMemo(
    () =>
      buildQboTxnSearchFilterFields({
        vendorOptions: qboVendorFieldOptions,
        categoryOptions: qboCategoryFieldOptions,
        locationOptions: qboLocationFieldOptions,
      }),
    [qboVendorFieldOptions, qboCategoryFieldOptions, qboLocationFieldOptions]
  )

  const filteredRows = useMemo(
    () => filterQboTxnRowsWithTree(rows, resultFilterTree, qboFilterFields),
    [rows, resultFilterTree, qboFilterFields]
  )

  const textFilteredRows = useMemo(
    () => filterTransactionRowsByText(filteredRows, resultTextSearch),
    [filteredRows, resultTextSearch]
  )

  const isConflictModeResults = useMemo(
    () =>
      Boolean(searchedCriteria?.category_conflicts_mode) ||
      (conflictGroups != null && conflictGroups.length > 0),
    [searchedCriteria?.category_conflicts_mode, conflictGroups]
  )

  const categoryConflictIgnoreSets = useMemo(
    () =>
      buildCategoryConflictIgnoreSets(
        ignoredGroups.map((g) => g.group_id),
        ignoredDescriptions
      ),
    [ignoredGroups, ignoredDescriptions]
  )

  const activeConflictRows = useMemo(
    () =>
      isConflictModeResults
        ? filterIgnoredConflictRows(textFilteredRows, categoryConflictIgnoreSets)
        : textFilteredRows,
    [isConflictModeResults, textFilteredRows, categoryConflictIgnoreSets]
  )

  const todoRows = useMemo(
    () =>
      isConflictModeResults ? filterResolvedConflictRows(activeConflictRows) : filteredRows,
    [isConflictModeResults, activeConflictRows, filteredRows]
  )

  const ignoredConflictCount = ignoredGroups.length + ignoredDescriptions.length

  const sortedData = useMemo(
    () => applySorts(todoRows, sortSpecs, QBO_TXN_FIELD_DEFINITIONS),
    [todoRows, sortSpecs]
  )

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs]
  )

  const handleRequestSort = useCallback((field: string) => {
    setHasUserSorted(true)
    setSortSpecs((prev) => {
      const existing = prev.find((entry) => entry.field === field)
      if (existing) {
        return [{ field, direction: existing.direction === 'asc' ? 'desc' : 'asc' }]
      }
      return [{ field, direction: 'asc' }]
    })
  }, [])

  const handleSortSpecsChange = useCallback((next: SortSpec[]) => {
    setHasUserSorted(true)
    setSortSpecs(next)
  }, [])

  const visibleColumnKeys = useMemo(
    () => getVisibleFieldKeys(QBO_TXN_FIELD_DEFINITIONS, columnVisibility),
    [columnVisibility]
  )

  const visibleColumnCount = visibleColumnKeys.length

  const handleColumnVisibilityChange = useCallback((visibility: ColumnVisibility) => {
    setColumnVisibility(visibility)
    saveColumnVisibility(QBO_TXN_SEARCH_PAGE_KEY, visibility)
  }, [])

  const groupOrder = useMemo(
    () => (conflictGroups ?? []).map((g) => g.group_id),
    [conflictGroups]
  )

  const conflictGroupViews = useMemo(
    () => buildConflictGroupViews(conflictGroups ?? [], todoRows),
    [conflictGroups, todoRows]
  )

  const conflictGroupViewById = useMemo(
    () => new Map(conflictGroupViews.map((v) => [v.groupId, v])),
    [conflictGroupViews]
  )

  /** Active-tab counts from filtered visible data; job stats only before rows/groups are loaded. */
  const displayedActiveConflictGroupCount =
    conflictGroups != null && conflictGroups.length > 0
      ? conflictGroupViews.length
      : (conflictStats?.groups_found ?? conflictGroupViews.length)

  const displayedActiveConflictLineCount =
    rows.length > 0 || todoRows.length > 0
      ? todoRows.length
      : (conflictStats?.rows_in_groups ?? todoRows.length)

  const displayBuckets = useMemo(() => {
    if (!isConflictModeResults) return null
    let buckets = bucketRowsByConflictGroup(sortedData, groupOrder)
    if (!hasUserSorted) {
      buckets = buckets.map((bucket) => ({
        ...bucket,
        rows: [...bucket.rows].sort(defaultConflictRowCompare),
      }))
    }
    return buckets
  }, [isConflictModeResults, sortedData, groupOrder, hasUserSorted])

  const sortedDataKeys = useMemo(() => sortedData.map(rowKey), [sortedData])

  useEffect(() => {
    migrateLegacyColumnWidths()
  }, [])

  const { getWidth, startResize, getStickyLeft } = useColumnWidths({
    pageKey: QBO_TXN_SEARCH_PAGE_KEY,
    columnKeys: [...ALL_COLUMN_KEYS],
    defaults: DEFAULT_WIDTHS,
  })

  const pinnedKeys = [...FROZEN_COLUMN_KEYS]

  const visibleTableWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth, visibleColumnKeys]
  )

  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: visibleTableWidth,
      minWidth: visibleTableWidth,
      tableLayout: 'fixed',
    }),
    [visibleTableWidth]
  )

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const width = getWidth(columnKey)
      const isPinned = isPinnedColumn(columnKey)
      const style: CSSProperties = {
        ...fixedColumnStyle(width),
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
      }
      if (isPinned) {
        style.left = getStickyLeft(pinnedKeys, columnKey)
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const width = getWidth(columnKey)
      const style: CSSProperties = { ...fixedColumnStyle(width), height: 1 }
      if (isPinnedColumn(columnKey)) {
        return {
          ...style,
          position: 'sticky',
          left: getStickyLeft(pinnedKeys, columnKey),
          zIndex: 5,
        }
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys]
  )

  const pendingChanges = useMemo(
    () => rows.filter(rowHasPendingChanges),
    [rows]
  )

  const pendingRowKeys = useMemo(
    () => new Set(pendingChanges.map((r) => rowKey(r))),
    [pendingChanges]
  )

  const uniqueTxnCount = useMemo(() => {
    return new Set(pendingChanges.map((r) => r.transaction_id)).size
  }, [pendingChanges])

  const lockedPendingChanges = useMemo(
    () => pendingChanges.filter((r) => isDateLocked(r.date, lockDate)),
    [pendingChanges, lockDate]
  )

  const lockedUniqueTxnCount = useMemo(
    () => new Set(lockedPendingChanges.map((r) => r.transaction_id)).size,
    [lockedPendingChanges]
  )

  const pendingHeaderLocationChanges = useMemo(
    () =>
      pendingChanges.filter(
        (r) => supportsHeaderLocationEdit(r.type) && r.line_item_location_new !== undefined
      ),
    [pendingChanges]
  )

  const pendingHeaderLocationTxnCount = useMemo(
    () => new Set(pendingHeaderLocationChanges.map((r) => r.transaction_id)).size,
    [pendingHeaderLocationChanges]
  )

  const pendingHeaderDocNumberChanges = useMemo(
    () => pendingChanges.filter((r) => r.doc_number_new !== undefined),
    [pendingChanges]
  )

  const pendingHeaderDocNumberTxnCount = useMemo(
    () => new Set(pendingHeaderDocNumberChanges.map((r) => r.transaction_id)).size,
    [pendingHeaderDocNumberChanges]
  )

  const hiddenPendingChangeCount = useMemo(() => {
    const visibleKeys = new Set(
      filteredRows.map((r) => `${r.transaction_id}\0${r.line_item_id}`)
    )
    return pendingChanges.filter(
      (r) => !visibleKeys.has(`${r.transaction_id}\0${r.line_item_id}`)
    ).length
  }, [pendingChanges, filteredRows])

  const hasActiveResultFilters = countFilterConditions(resultFilterTree) > 0
  const activeResultFilterCount = countFilterConditions(resultFilterTree)
  const resultFilterPills = useMemo(
    () => generateFilterPills(resultFilterTree, qboFilterFields),
    [resultFilterTree, qboFilterFields]
  )
  const hasActiveTextSearch = resultTextSearch.trim().length > 0
  const showingResultSubset =
    hasActiveResultFilters ||
    hasActiveTextSearch ||
    filteredRows.length !== rows.length ||
    textFilteredRows.length !== filteredRows.length

  const showTabResults = showResults

  function selectConflictViewTab(tab: ConflictViewTabId) {
    if (tab !== conflictViewTab) {
      setSelectedRowKeys(new Set())
      selectionAnchorKeyRef.current = null
      suppressCheckboxChangeForRowKeyRef.current = null
      setBulkEditOpen(false)
    }
    setConflictViewTab(tab)
  }

  const showConflictActiveView = !isConflictModeResults || conflictViewTab === 'active'

  const searchCriteriaStale =
    showTabResults &&
    searchedCriteria != null &&
    !criteriaEqual(criteria, searchedCriteria)

  useEffect(() => {
    if (showTabResults) {
      setSearchFormExpanded(false)
    }
  }, [showTabResults])

  useEffect(() => {
    setShowResultFilters(false)
  }, [activeTab])

  useEffect(() => {
    if (!showTabResults || activeTab !== 'conflicts') return

    let cancelled = false
    setIgnoresLoading(true)
    void getCategoryConflictIgnores()
      .then((data) => {
        if (!cancelled) {
          setIgnoredGroups(data.ignored_groups)
          setIgnoredDescriptions(data.ignored_descriptions)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(formatApiError(err, 'Failed to load ignore settings'))
        }
      })
      .finally(() => {
        if (!cancelled) setIgnoresLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [showTabResults, activeTab])

  function selectSearchTab(tab: QboTxnSearchTabId) {
    if (tab !== activeTab) {
      // Selection / bulk-edit are component-local and keyed by row identity —
      // clear them so one tab's selection cannot leak into the other.
      setSelectedRowKeys(new Set())
      selectionAnchorKeyRef.current = null
      suppressCheckboxChangeForRowKeyRef.current = null
      setBulkEditOpen(false)
      setCollapsedGroupIds(new Set())
      setConflictViewTab('active')
    }
    if (tab === 'conflicts') {
      const conflictCriteria = useQboTransactionSearchStore.getState().conflicts.criteria
      if (!conflictCriteria.date_start?.trim() && !conflictCriteria.date_end?.trim()) {
        const { start, end } = getTrailingTwelveMonthsDateRange()
        setCriteria(
          (prev) => ({
            ...prev,
            date_start: start,
            date_end: end,
            category_conflicts_mode: true,
          }),
          'conflicts'
        )
      }
    }
    setActiveTab(tab)
    const targetSlice =
      tab === 'conflicts'
        ? useQboTransactionSearchStore.getState().conflicts
        : useQboTransactionSearchStore.getState().basic
    setSearchFormExpanded(!targetSlice.showResults)
  }

  useEffect(() => {
    expireIfStale()
  }, [expireIfStale])

  useEffect(() => {
    void getSettings()
      .then(({ settings }) => {
        setLockDate(settings[QBO_TXN_SEARCH_LOCK_DATE_KEY]?.value ?? '')
      })
      .catch(() => {
        // Non-fatal: lock date defaults to unset
      })
      .finally(() => setLockDateLoading(false))
  }, [])

  async function persistLockDate(value: string) {
    setLockDateSaving(true)
    try {
      await updateSetting(QBO_TXN_SEARCH_LOCK_DATE_KEY, {
        value,
        description:
          'QBO transaction search lock date — transactions before this date require an override before applying to QuickBooks (shared for all users)',
      })
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save lock date'))
    } finally {
      setLockDateSaving(false)
    }
  }

  async function handleLockDateChange(value: string) {
    setLockDate(value)
    await persistLockDate(value)
  }

  const pollJob = useCallback(
    async (jobId: number, onComplete: (job: Awaited<ReturnType<typeof getJobStatus>>) => void) => {
      const interval = window.setInterval(async () => {
        try {
          const job = await getJobStatus(jobId)
          registerServerJob(job)
          if (job.status === 'completed' || job.status === 'failed') {
            window.clearInterval(interval)
            onComplete(job)
          }
        } catch (err) {
          console.error('Job poll failed:', err)
        }
      }, 1500)
    },
    [registerServerJob]
  )

  useEffect(() => {
    const searchJob = searchJobId ? serverById[searchJobId]?.job : null
    if (searchJob?.status === 'failed') {
      setSearching(false)
    }
  }, [searchJobId, serverById])

  useEffect(() => {
    const applyJob = applyJobId ? serverById[applyJobId]?.job : null
    if (!applyJob) return
    if (applyJob.status === 'completed') {
      const statuses = getApplyResultsFromJob(applyJob)
      if (statuses) {
        const applyTab = applyTabRef.current
        const currentRows =
          applyTab === 'conflicts'
            ? useQboTransactionSearchStore.getState().conflicts.rows
            : useQboTransactionSearchStore.getState().basic.rows
        updateRows(mergeApplyStatusesIntoRows(currentRows, statuses), applyTab)
      }
      setApplying(false)
    }
    if (applyJob.status === 'failed') {
      setApplying(false)
    }
  }, [applyJobId, serverById, updateRows])

  useEffect(() => {
    if (!bulkEditOpen) return
    setBulkApplyCategory(isConflictModeResults)
    setBulkApplyDescription(false)
    setBulkApplyLocation(false)
    setBulkCategoryNew(null)
    setBulkDescriptionNew('')
    setBulkLocationNew(null)
  }, [bulkEditOpen, isConflictModeResults])

  function updateCriteria<K extends keyof QboTransactionSearchCriteria>(
    key: K,
    value: QboTransactionSearchCriteria[K]
  ) {
    setCriteria((prev) => ({ ...prev, [key]: value }))
  }

  function updateRow(row: QboTransactionSearchRow, patch: Partial<QboTransactionSearchRow>) {
    updateRows(
      rows.map((r) =>
        r.transaction_id === row.transaction_id && r.line_item_id === row.line_item_id
          ? { ...r, ...patch }
          : r
      )
    )
  }

  function commitDocNumberEdit(
    row: QboTransactionSearchRow,
    patch: Pick<QboTransactionSearchRow, 'doc_number_new'>
  ) {
    updateRows(
      rows.map((r) =>
        r.transaction_id === row.transaction_id ? { ...r, ...patch } : r
      )
    )
  }

  function discardPendingEditsForKeys(keys: Set<string>) {
    if (keys.size === 0) return
    let discarded = 0
    updateRows(
      rows.map((r) => {
        if (!keys.has(rowKey(r))) return r
        if (!pendingRowKeys.has(rowKey(r))) return r
        discarded += 1
        return clearPendingRowEdits(r)
      })
    )
    if (discarded > 0) {
      toast.success(
        `Discarded pending edits on ${discarded} line${discarded !== 1 ? 's' : ''}`
      )
    }
  }

  const allVisibleRowsSelected = useMemo(
    () =>
      sortedData.length > 0 && sortedData.every((r) => selectedRowKeys.has(rowKey(r))),
    [sortedData, selectedRowKeys]
  )

  const someVisibleRowsSelected = useMemo(
    () => sortedData.some((r) => selectedRowKeys.has(rowKey(r))),
    [sortedData, selectedRowKeys]
  )

  const selectedPendingEditCount = useMemo(
    () =>
      [...selectedRowKeys].filter((key) => pendingRowKeys.has(key)).length,
    [selectedRowKeys, pendingRowKeys]
  )

  const selectedUniqueQboUrls = useMemo(() => {
    const selectedRows = rows.filter((r) => selectedRowKeys.has(rowKey(r)))
    return uniqueQboUrlsFromSelectedRows(selectedRows)
  }, [rows, selectedRowKeys])

  useEffect(() => {
    const el = headerSelectAllCheckboxRef.current
    if (!el) return
    el.indeterminate = someVisibleRowsSelected && !allVisibleRowsSelected
  }, [someVisibleRowsSelected, allVisibleRowsSelected])

  function armSuppressCheckboxChangeForRow(key: string) {
    suppressCheckboxChangeForRowKeyRef.current = key
  }

  function toggleSelectAll() {
    if (allVisibleRowsSelected) {
      setSelectedRowKeys(new Set())
      selectionAnchorKeyRef.current = null
      return
    }
    setSelectedRowKeys(new Set(sortedData.map(rowKey)))
    const firstRow = sortedData[0]
    selectionAnchorKeyRef.current = firstRow ? rowKey(firstRow) : null
  }

  function toggleGroupSelection(groupRows: QboTransactionSearchRow[]) {
    if (groupRows.length === 0) return
    const allSelected = groupRows.every((r) => selectedRowKeys.has(rowKey(r)))
    setSelectedRowKeys((prev) => {
      const next = new Set(prev)
      for (const row of groupRows) {
        const key = rowKey(row)
        if (allSelected) {
          next.delete(key)
        } else {
          next.add(key)
        }
      }
      return next
    })
  }

  function toggleGroupCollapsed(groupId: string) {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const visibleConflictGroupIds = useMemo(
    () => (displayBuckets ?? []).map((bucket) => bucket.groupId),
    [displayBuckets]
  )

  const allConflictGroupsCollapsed =
    visibleConflictGroupIds.length > 0 &&
    visibleConflictGroupIds.every((id) => collapsedGroupIds.has(id))

  function collapseAllConflictGroups() {
    setCollapsedGroupIds(new Set(visibleConflictGroupIds))
  }

  function expandAllConflictGroups() {
    setCollapsedGroupIds(new Set())
  }

  const handleSelectionCheckboxMouseDown = useCallback(
    (
      row: QboTransactionSearchRow,
      orderedKeys: string[],
      e: React.MouseEvent<HTMLInputElement>
    ) => {
      e.stopPropagation()
      const anchorKey = selectionAnchorKeyRef.current
      if (!e.shiftKey || anchorKey === null) return
      e.preventDefault()
      const targetKey = rowKey(row)
      const rangeKeys = keysInVisibleRange(orderedKeys, anchorKey, targetKey)
      if (rangeKeys.length === 0) return
      armSuppressCheckboxChangeForRow(targetKey)
      setSelectedRowKeys((prev) => {
        const next = new Set(prev)
        for (const key of rangeKeys) {
          next.add(key)
        }
        return next
      })
    },
    []
  )

  const handleSelectionCheckboxChange = useCallback(
    (row: QboTransactionSearchRow, e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      const key = rowKey(row)
      if (suppressCheckboxChangeForRowKeyRef.current === key) {
        const suppressedKey = key
        setTimeout(() => {
          if (suppressCheckboxChangeForRowKeyRef.current === suppressedKey) {
            suppressCheckboxChangeForRowKeyRef.current = null
          }
        }, 0)
        return
      }
      selectionAnchorKeyRef.current = key
      const checked = e.target.checked
      setSelectedRowKeys((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(key)
        } else {
          next.delete(key)
        }
        return next
      })
    },
    []
  )

  function openBulkEditForRows(rowsToSelect: QboTransactionSearchRow[]) {
    setSelectedRowKeys(new Set(rowsToSelect.map(rowKey)))
    const firstRow = rowsToSelect[0]
    selectionAnchorKeyRef.current = firstRow ? rowKey(firstRow) : null
    setBulkEditOpen(true)
  }

  async function persistCategoryConflictIgnores(
    groups: IgnoredConflictGroupEntry[],
    descriptions: IgnoredConflictDescriptionEntry[]
  ): Promise<void> {
    setIgnoresSaving(true)
    try {
      const saved = await putCategoryConflictIgnores({
        ignored_groups: groups,
        ignored_descriptions: descriptions,
      })
      setIgnoredGroups(saved.ignored_groups)
      setIgnoredDescriptions(saved.ignored_descriptions)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save ignore settings'))
      throw err
    } finally {
      setIgnoresSaving(false)
    }
  }

  async function handleIgnoreGroup(view: ConflictGroupView) {
    if (ignoresSaving) return
    if (ignoredGroups.some((g) => g.group_id === view.groupId)) return

    const nextGroups: IgnoredConflictGroupEntry[] = [
      ...ignoredGroups,
      {
        group_id: view.groupId,
        contact_name: view.contactName,
        representative_description: view.representativeDescription,
      },
    ]
    try {
      await persistCategoryConflictIgnores(nextGroups, ignoredDescriptions)
      toast.success('Group ignored')
    } catch {
      // toast shown in persistCategoryConflictIgnores
    }
  }

  async function handleIgnoreSelectedDescriptions() {
    if (ignoresSaving || selectedRowKeys.size === 0) return

    const selectedRows = rows.filter((r) => selectedRowKeys.has(rowKey(r)))
    const newEntries = buildIgnoredDescriptionEntriesFromRows(selectedRows)
    if (newEntries.length === 0) {
      toast.error('No ignorable descriptions in selection')
      return
    }

    const existingKeys = new Set(
      ignoredDescriptions.map((entry) =>
        `${entry.contact_name.trim()}\0${entry.normalized_description.trim()}`
      )
    )
    const toAdd = newEntries.filter(
      (entry) =>
        !existingKeys.has(`${entry.contact_name}\0${entry.normalized_description}`)
    )
    if (toAdd.length === 0) {
      toast.success('Selected descriptions are already ignored')
      return
    }

    try {
      await persistCategoryConflictIgnores(ignoredGroups, [...ignoredDescriptions, ...toAdd])
      toast.success(
        `Ignored ${toAdd.length} description${toAdd.length !== 1 ? 's' : ''}`
      )
    } catch {
      // toast shown in persistCategoryConflictIgnores
    }
  }

  function handleOpenSelectedInQuickBooks() {
    if (selectedRowKeys.size === 0) return

    const selectedRows = rows.filter((r) => selectedRowKeys.has(rowKey(r)))
    const urls = uniqueQboUrlsFromSelectedRows(selectedRows)
    if (urls.length === 0) {
      toast.error('No QuickBooks links available for the selected rows')
      return
    }

    const { opened, blocked } = openQboUrls(urls)
    if (blocked > 0) {
      toast.warning(
        `Opened ${opened} of ${urls.length} transaction(s) in QuickBooks. Your browser blocked ${blocked} popup(s).`
      )
    } else {
      toast.success(`Opened ${opened} transaction(s) in QuickBooks`)
    }
  }

  async function handleUnignoreGroup(groupId: string) {
    if (ignoresSaving) return
    const nextGroups = ignoredGroups.filter((g) => g.group_id !== groupId)
    try {
      await persistCategoryConflictIgnores(nextGroups, ignoredDescriptions)
      toast.success('Group unignored — run Search again to restore it on Active')
    } catch {
      // toast shown in persistCategoryConflictIgnores
    }
  }

  async function handleUnignoreDescription(entry: IgnoredConflictDescriptionEntry) {
    if (ignoresSaving) return
    const key = `${entry.contact_name.trim()}\0${entry.normalized_description.trim()}`
    const nextDescriptions = ignoredDescriptions.filter(
      (d) => `${d.contact_name.trim()}\0${d.normalized_description.trim()}` !== key
    )
    try {
      await persistCategoryConflictIgnores(ignoredGroups, nextDescriptions)
      toast.success('Description unignored — run Search again to restore it on Active')
    } catch {
      // toast shown in persistCategoryConflictIgnores
    }
  }

  function handleBulkEditApply() {
    const applies = bulkApplyCategory || bulkApplyDescription || bulkApplyLocation
    if (!applies) {
      toast.error('Select at least one field to update')
      return
    }
    if (bulkApplyCategory && !bulkCategoryNew?.trim()) {
      toast.error('Choose a category, or uncheck Category')
      return
    }
    if (bulkApplyLocation && bulkLocationNew === null) {
      toast.error('Choose a location, or uncheck Location')
      return
    }
    if (selectedRowKeys.size === 0) return

    updateRows(
      rows.map((r) => {
        if (!selectedRowKeys.has(rowKey(r))) return r
        let next = r
        if (bulkApplyCategory) {
          next = { ...next, ...queueCategoryEdit(next, bulkCategoryNew) }
        }
        if (bulkApplyDescription) {
          next = { ...next, ...queueDescriptionEdit(next, bulkDescriptionNew) }
        }
        if (bulkApplyLocation && supportsHeaderLocationEdit(r.type)) {
          next = { ...next, ...queueLocationEdit(next, bulkLocationNew) }
        }
        return next
      })
    )

    const labels: string[] = []
    if (bulkApplyCategory) labels.push('category')
    if (bulkApplyDescription) labels.push('description')
    if (bulkApplyLocation) labels.push('location')
    toast.success(
      `Updated ${labels.join(' and ')} on ${selectedRowKeys.size} line${selectedRowKeys.size !== 1 ? 's' : ''}`
    )
    setBulkEditOpen(false)
    setSelectedRowKeys(new Set())
    selectionAnchorKeyRef.current = null
  }

  async function handleSearch() {
    const searchCriteria =
      activeTab === 'conflicts'
        ? { ...criteria, category_conflicts_mode: true as const }
        : { ...criteria, category_conflicts_mode: false as const }

    if (searchCriteria.category_conflicts_mode) {
      const startEmpty = !searchCriteria.date_start?.trim()
      const endEmpty = !searchCriteria.date_end?.trim()
      if (startEmpty && endEmpty) {
        const { start, end } = getTrailingTwelveMonthsDateRange()
        setCriteria(
          (prev) => ({ ...prev, date_start: start, date_end: end }),
          'conflicts'
        )
        toast.warning(
          'Date range prefilled to the last 12 months. Review the dates and click Search again.'
        )
        return
      }
      if (startEmpty || endEmpty) {
        toast.error('Start date and end date are required for inconsistent category search.')
        return
      }
    }

    try {
      setSearching(true)
      setHasUserSorted(false)
      setSortSpecs(QBO_TXN_DEFAULT_SORT)
      setCollapsedGroupIds(new Set())
      setSelectedRowKeys(new Set())
      selectionAnchorKeyRef.current = null
      const result = await startQboTransactionSearch(searchCriteria)
      setSearchJobId(result.job_id)
      registerServerJob({
        id: result.job_id,
        job_type: 'qbo_transaction_search',
        status: 'pending',
        total_items: result.total_items,
        processed_items: 0,
        successful_items: 0,
        failed_items: 0,
        created_at: new Date().toISOString(),
      })
      void pollJob(result.job_id, (job) => {
        if (job.status === 'completed') {
          setSearchResults(
            getSearchResultsFromJob(job),
            searchCriteria,
            getSearchStatsFromJob(job),
            getConflictGroupsFromJob(job),
            getConflictStatsFromJob(job)
          )
        }
        setSearching(false)
      })
    } catch (err) {
      setSearching(false)
      toast.error(formatApiError(err, 'Failed to start search'))
    }
  }

  function handleReset() {
    resetSearch()
    setSearchJobId(null)
    setApplyJobId(null)
    setShowApplyConfirm(false)
    setLockOverrideAcknowledged(false)
    setSearchFormExpanded(true)
    setSelectedRowKeys(new Set())
    setBulkEditOpen(false)
    setHasUserSorted(false)
    setSortSpecs(QBO_TXN_DEFAULT_SORT)
    setCollapsedGroupIds(new Set())
    selectionAnchorKeyRef.current = null
  }

  async function handleApply() {
    const changes = pendingChanges.map((r) => ({
      transaction_id: r.transaction_id,
      type: r.type,
      line_item_id: r.line_item_id,
      line_item_description: r.line_item_description,
      ...buildApplyPayloadFields(r),
    }))
    if (changes.length === 0) {
      toast.error('No pending changes to apply')
      return
    }
    try {
      setApplying(true)
      setShowApplyConfirm(false)
      applyTabRef.current = activeTab
      const result = await startQboTransactionApply(changes, false)
      setApplyJobId(result.job_id)
      registerServerJob({
        id: result.job_id,
        job_type: 'qbo_transaction_apply',
        status: 'pending',
        total_items: result.total_items,
        processed_items: 0,
        successful_items: 0,
        failed_items: 0,
        created_at: new Date().toISOString(),
      })
      void pollJob(result.job_id, () => setApplying(false))
    } catch (err) {
      setApplying(false)
      toast.error(formatApiError(err, 'Failed to start apply'))
    }
  }

  function renderTransactionCell(
    columnKey: QboTxnColumnKey,
    row: QboTransactionSearchRow,
    index: number,
    orderedKeys: string[]
  ): ReactNode {
    const locked = isDateLocked(row.date, lockDate)
    const key = rowKey(row)
    const isSelected = selectedRowKeys.has(key)
    const hasPendingChange = pendingRowKeys.has(key)

    switch (columnKey) {
      case 'select':
        return (
          <td className={pinnedCellClass('select')} style={cellStyle('select')}>
            <div className="flex h-full min-h-9 items-center">
              <input
                type="checkbox"
                checked={isSelected}
                onMouseDown={(e) => handleSelectionCheckboxMouseDown(row, orderedKeys, e)}
                onChange={(e) => handleSelectionCheckboxChange(row, e)}
                className="inline-block origin-center scale-110 rounded border-gray-300 align-middle"
                aria-label={`Select line ${index + 1}`}
              />
            </div>
          </td>
        )
      case 'transaction_id':
        return (
          <td
            className={`tabular-nums ${pinnedCellClass('transaction_id')}`}
            style={cellStyle('transaction_id')}
          >
            <div className={TXN_READONLY_CELL}>
              {row.url ? (
                <a
                  href={row.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                  title="Open in QuickBooks"
                >
                  {row.transaction_id}
                </a>
              ) : (
                row.transaction_id
              )}
            </div>
          </td>
        )
      case 'doc_number':
        return (
          <td className={pinnedCellClass('doc_number')} style={cellStyle('doc_number')}>
            <DocNumberInput
              row={row}
              onCommit={(patch) => commitDocNumberEdit(row, patch)}
            />
          </td>
        )
      case 'date':
        return (
          <td
            className={`whitespace-nowrap ${pinnedCellClass('date')}`}
            style={cellStyle('date')}
          >
            <div className={TXN_READONLY_CELL}>
              <span className="inline-flex items-center gap-1">
                {hasPendingChange ? (
                  <span
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                    aria-label="Pending changes"
                    title="Pending changes — not yet applied"
                  />
                ) : null}
                {row.date}
                {locked ? (
                  <LockClosedIcon
                    className="h-3.5 w-3.5 shrink-0 text-amber-500"
                    aria-label="Locked period"
                    title={`Dated before your lock date (${lockDate}) — applying changes will require an override`}
                  />
                ) : null}
              </span>
            </div>
          </td>
        )
      case 'type':
        return (
          <td className={pinnedCellClass('type')} style={cellStyle('type')}>
            <div className={TXN_READONLY_CELL}>{row.type}</div>
          </td>
        )
      case 'contact_name':
        return (
          <td
            className={pinnedCellClass('contact_name')}
            style={cellStyle('contact_name')}
          >
            <div className={TXN_READONLY_CELL}>
              <span className="block truncate" title={row.contact_name}>
                {row.contact_name}
              </span>
            </div>
          </td>
        )
      case 'line_item_category': {
        const categoryValue = displayCategory(row)
        return (
          <td style={cellStyle('line_item_category')}>
            <SearchableSelect
              options={selectOptionsWithValue(tableCategoryOptions, categoryValue)}
              value={categoryValue || null}
              onChange={(id) => updateRow(row, queueCategoryEdit(row, id))}
              placeholder="Category…"
              allowClear
              size="compact"
              preferBelow
              useViewportForPlacement
              showDetailsInTrigger
              className="w-full"
            />
          </td>
        )
      }
      case 'line_item_description':
        return (
          <td style={cellStyle('line_item_description')}>
            <LineDescriptionInput
              row={row}
              onCommit={(patch) => updateRow(row, patch)}
            />
          </td>
        )
      case 'line_item_location': {
        const locationValue = displayLocation(row)
        return (
          <td style={cellStyle('line_item_location')}>
            {supportsHeaderLocationEdit(row.type) ? (
              <SearchableSelect
                options={selectOptionsWithValue(tableLocationOptions, locationValue)}
                value={locationValue}
                onChange={(id) => updateRow(row, queueLocationEdit(row, id))}
                placeholder="Location…"
                allowClear
                size="compact"
                preferBelow
                useViewportForPlacement
                fillHeight
                className="h-full w-full"
              />
            ) : (
              <div className={TXN_READONLY_CELL}>
                <span className="text-xs text-gray-400">—</span>
              </div>
            )}
          </td>
        )
      }
      case 'line_item_amount':
        return (
          <td className="tabular-nums text-right" style={cellStyle('line_item_amount')}>
            <div className={`${TXN_READONLY_CELL} justify-end`}>
              {formatAmount(row.line_item_amount)}
            </div>
          </td>
        )
      case 'update_status':
        return (
          <td style={cellStyle('update_status')}>
            <div className={TXN_READONLY_CELL}>
              <div className="flex items-center gap-1">
                <span className="text-xs">{row.update_status || '—'}</span>
                {hasPendingChange ? (
                  <button
                    type="button"
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    onClick={() => discardPendingEditsForKeys(new Set([key]))}
                    title="Discard pending edits"
                    aria-label="Discard pending edits"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                {row.update_status ? (
                  <button
                    type="button"
                    className="text-[10px] text-gray-500 hover:text-gray-800"
                    onClick={() => updateRow(row, { update_status: '' })}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
          </td>
        )
      default:
        return null
    }
  }

  function renderTransactionRow(
    row: QboTransactionSearchRow,
    index: number,
    orderedKeys: string[]
  ) {
    const key = rowKey(row)
    const hasPendingChange = pendingRowKeys.has(key)
    return (
      <tr
        key={key}
        className={`${hasPendingChange ? 'table-row-pending' : ''} ${
          isConflictModeResults ? 'border-l-2 border-l-amber-400' : ''
        } [&>td]:align-middle [&>td]:!py-1 [&>td]:!px-1.5`.trim()}
        title={hasPendingChange ? 'Pending changes — not yet applied' : undefined}
      >
        {visibleColumnKeys.map((columnKey) =>
          renderTransactionCell(columnKey as QboTxnColumnKey, row, index, orderedKeys)
        )}
      </tr>
    )
  }

  function renderTableHeaderCell(columnKey: QboTxnColumnKey): ReactNode {
    if (columnKey === 'select') {
      return (
        <th
          key={columnKey}
          className={`relative ${pinnedHeaderClass(columnKey)}`}
          style={headerStyle(columnKey)}
        >
          <input
            ref={headerSelectAllCheckboxRef}
            type="checkbox"
            checked={allVisibleRowsSelected}
            onChange={toggleSelectAll}
            className="inline-block origin-center scale-110 rounded border-gray-300 align-middle"
            aria-label="Select all visible rows"
          />
          <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
        </th>
      )
    }

    const label = QBO_TXN_COLUMN_LABELS[columnKey]
    const alignRight = columnKey === 'line_item_amount'

    if (isQboTxnColumnSortable(columnKey)) {
      return (
        <TxnSortableHeader
          key={columnKey}
          label={label}
          sortKey={columnKey}
          sortDirection={getSortDirection(columnKey)}
          onSort={handleRequestSort}
          columnKey={columnKey}
          style={headerStyle(columnKey)}
          onStartResize={startResize}
          className={pinnedHeaderClass(columnKey)}
          alignRight={alignRight}
        />
      )
    }

    return (
      <th key={columnKey} className={`relative ${pinnedHeaderClass(columnKey)}`} style={headerStyle(columnKey)}>
        <span className={`inline-flex items-center pr-2 ${alignRight ? 'justify-end w-full' : ''}`}>
          {label}
        </span>
        <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
      </th>
    )
  }

  return (
    <AccountingToolPage
      title="QBO Transactions"
      description="Search QuickBooks transactions and recategorize line items in bulk"
      headerExtra={
        <SearchTabNav activeTab={activeTab} onSelect={selectSearchTab} />
      }
    >
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm shadow-gray-950/[0.04]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="container-title">
              {activeTab === 'conflicts'
                ? 'Find inconsistent categories'
                : 'Search QuickBooks'}
            </h2>
            {showTabResults && !searchFormExpanded && searchedCriteria ? (
              <SearchCriteriaSummary criteria={searchedCriteria} />
            ) : (
              <p className="mt-0.5 text-xs text-gray-500">
                {activeTab === 'conflicts'
                  ? 'Surfaces same-vendor line descriptions categorized under more than one QBO category. Click Search to load or refresh results.'
                  : 'Queries QuickBooks. Click Search to load or refresh results.'}
              </p>
            )}
          </div>
          {showTabResults ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn btn-ghost text-sm"
                onClick={() => setSearchFormExpanded((v) => !v)}
              >
                {searchFormExpanded ? (
                  <>
                    <ChevronUpIcon className="h-4 w-4" />
                    Hide
                  </>
                ) : (
                  <>
                    <PencilSquareIcon className="h-4 w-4" />
                    Edit search
                  </>
                )}
              </button>
              {!searchFormExpanded ? (
                <button
                  type="button"
                  className="btn btn-secondary text-sm"
                  onClick={handleReset}
                  disabled={searching}
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Reset
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {searchCriteriaStale ? (
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Search criteria changed — click Search to refresh results.
          </p>
        ) : null}

        {(!showTabResults || searchFormExpanded) && (
          <>
            <div className="mt-4">
              {activeTab === 'conflicts' ? (
                <TxnConflictSearchBar
                  criteria={criteria}
                  onChange={updateCriteria}
                  idPrefix="txn-conflict"
                />
              ) : (
                <TxnSearchFilterBar
                  criteria={criteria}
                  onChange={updateCriteria}
                  idPrefix="txn-search"
                  vendorOptions={vendorOptions}
                  categoryOptions={categoryOptions}
                  locationOptions={locationOptions}
                />
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary text-sm"
                onClick={() => void handleSearch()}
                disabled={searching}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                {searching ? 'Searching…' : 'Search'}
              </button>
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={handleReset}
                disabled={searching}
              >
                <ArrowPathIcon className="h-4 w-4" />
                Reset
              </button>
            </div>
          </>
        )}
      </div>

      {showTabResults ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {rows.length > 0 && showConflictActiveView ? (
            <div className="flex-shrink-0 rounded-xl border border-gray-200 bg-white px-4 py-3">
              <div className="flex min-w-0 flex-col gap-1">
                <span
                  className={`text-xs font-medium ${lockDate ? 'text-blue-600' : 'text-gray-600'}`}
                >
                  <HoverHelp label="Lock date" aria-label="About lock date">
                    <p>
                      Transactions dated before this date are treated as part of a closed month
                      or year. Editing them is still allowed, but applying changes to QuickBooks
                      for a locked transaction will show a warning that must be explicitly
                      overridden. Saved for all users in Bookkeeper; does not block anything on
                      the QuickBooks side.
                    </p>
                  </HoverHelp>
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1">
                    <DateInput
                      className="input-sm"
                      value={lockDate}
                      onChange={(v) => void handleLockDateChange(v)}
                      dateFormat="iso"
                      placeholder="yyyy-mm-dd"
                      disabled={
                        !lockDateUnlocked || lockDateLoading || lockDateSaving
                      }
                    />
                    <button
                      type="button"
                      className={`rounded p-1 ${
                        lockDateUnlocked
                          ? 'text-blue-600 hover:bg-blue-50'
                          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                      }`}
                      aria-label={
                        lockDateUnlocked
                          ? 'Lock lock date'
                          : 'Unlock to edit lock date'
                      }
                      disabled={lockDateLoading || lockDateSaving}
                      onClick={() => setLockDateUnlocked((v) => !v)}
                    >
                      {lockDateUnlocked ? (
                        <LockOpenIcon className="h-4 w-4" />
                      ) : (
                        <LockClosedIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Used to prohibit accidental edits to transactions dated before this date.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {isConflictModeResults && rows.length > 0 ? (
            <nav
              className="flex flex-shrink-0 gap-3 overflow-x-auto border-b border-gray-200 sm:gap-6"
              aria-label="Conflict results view"
            >
              <button
                type="button"
                onClick={() => selectConflictViewTab('active')}
                className={`whitespace-nowrap border-b-2 py-2 text-sm font-medium transition-colors ${
                  conflictViewTab === 'active'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                Active ({displayedActiveConflictGroupCount} group
                {displayedActiveConflictGroupCount !== 1 ? 's' : ''})
              </button>
              <button
                type="button"
                onClick={() => selectConflictViewTab('ignored')}
                className={`whitespace-nowrap border-b-2 py-2 text-sm font-medium transition-colors ${
                  conflictViewTab === 'ignored'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                Ignored ({ignoredConflictCount})
              </button>
            </nav>
          ) : null}

          {rows.length > 0 && showConflictActiveView ? (
            <TablePageShell
              className="min-h-0 flex-1"
              rowCount={
                <div className="mb-2 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 sm:text-sm">
                  {isConflictModeResults ? (
                    <span>
                      <span className="font-medium text-gray-700">
                        {displayedActiveConflictGroupCount} conflict group
                        {displayedActiveConflictGroupCount !== 1 ? 's' : ''} ·{' '}
                        {sortedData.length} line{sortedData.length !== 1 ? 's' : ''}{' '}
                        {showingResultSubset ? 'shown' : ''}
                      </span>
                      {showingResultSubset ? (
                        <span className="ml-1">(of {rows.length.toLocaleString()} total)</span>
                      ) : null}
                      {displayedActiveConflictLineCount !== activeConflictRows.length
                        ? ` (${activeConflictRows.length - displayedActiveConflictLineCount} resolved hidden)`
                        : ''}
                      {ignoresLoading ? ' · loading ignores…' : null}
                    </span>
                  ) : (
                    <span>
                      <span className="font-medium text-gray-700">
                        {showingResultSubset
                          ? `${sortedData.length} line${sortedData.length !== 1 ? 's' : ''} shown`
                          : `${rows.length} line${rows.length !== 1 ? 's' : ''} found`}
                      </span>
                      {showingResultSubset ? (
                        <span className="ml-1">(of {rows.length.toLocaleString()} total)</span>
                      ) : null}
                    </span>
                  )}
                  {searchStats?.complete && searchStats.transactions_scanned > 0 ? (
                    <span>
                      · searched {searchStats.transactions_scanned.toLocaleString()} transaction
                      {searchStats.transactions_scanned !== 1 ? 's' : ''} (all matching types in
                      range)
                    </span>
                  ) : null}
                  {selectedRowKeys.size > 0 ? (
                    <span>· {selectedRowKeys.size} selected</span>
                  ) : null}
                  {pendingChanges.length > 0 ? (
                    <span>
                      · {pendingChanges.length} line{pendingChanges.length !== 1 ? 's' : ''} with
                      changes ({uniqueTxnCount} transaction{uniqueTxnCount !== 1 ? 's' : ''})
                      {hasActiveResultFilters && hiddenPendingChangeCount > 0
                        ? ` · ${hiddenPendingChangeCount} hidden by refinement`
                        : ''}
                    </span>
                  ) : null}
                  {lockedPendingChanges.length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <LockClosedIcon className="h-3.5 w-3.5" />
                      {lockedPendingChanges.length} in a locked period
                    </span>
                  ) : null}
                </div>
              }
              toolbarLeft={
                <SearchInput
                  value={resultTextSearch}
                  onChange={setResultTextSearch}
                  placeholder="Filter results…"
                  className="max-w-md flex-1"
                />
              }
              toolbarRight={
                <div className="flex flex-wrap items-center gap-2">
                  {selectedRowKeys.size > 0 ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary text-sm"
                        title={`Bulk edit ${selectedRowKeys.size} selected line${selectedRowKeys.size !== 1 ? 's' : ''}`}
                        aria-label={`Bulk edit ${selectedRowKeys.size} selected line${selectedRowKeys.size !== 1 ? 's' : ''}`}
                        onClick={() => setBulkEditOpen(true)}
                      >
                        Bulk edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary text-sm"
                        title={`Open ${selectedUniqueQboUrls.length} unique transaction${selectedUniqueQboUrls.length !== 1 ? 's' : ''} in QuickBooks`}
                        aria-label={`Open ${selectedUniqueQboUrls.length} unique transaction${selectedUniqueQboUrls.length !== 1 ? 's' : ''} in QuickBooks`}
                        onClick={handleOpenSelectedInQuickBooks}
                      >
                        Open
                      </button>
                    </>
                  ) : null}
                  {isConflictModeResults && selectedRowKeys.size > 0 ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      title={`Ignore descriptions for ${selectedRowKeys.size} selected line${selectedRowKeys.size !== 1 ? 's' : ''}`}
                      aria-label={`Ignore descriptions for ${selectedRowKeys.size} selected line${selectedRowKeys.size !== 1 ? 's' : ''}`}
                      onClick={() => void handleIgnoreSelectedDescriptions()}
                      disabled={ignoresSaving}
                    >
                      Ignore descriptions
                    </button>
                  ) : null}
                  {selectedPendingEditCount > 0 ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      title={`Discard pending edits on ${selectedPendingEditCount} selected line${selectedPendingEditCount !== 1 ? 's' : ''}`}
                      aria-label={`Discard pending edits on ${selectedPendingEditCount} selected line${selectedPendingEditCount !== 1 ? 's' : ''}`}
                      onClick={() => discardPendingEditsForKeys(selectedRowKeys)}
                    >
                      Discard edits
                    </button>
                  ) : null}
                  {isConflictModeResults && visibleConflictGroupIds.length > 0 ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      onClick={() =>
                        allConflictGroupsCollapsed
                          ? expandAllConflictGroups()
                          : collapseAllConflictGroups()
                      }
                    >
                      {allConflictGroupsCollapsed ? 'Expand all' : 'Collapse all'}
                    </button>
                  ) : null}
                  {pendingChanges.length > 0 ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      title="Discard all pending edits"
                      aria-label="Discard all pending edits"
                      onClick={() => discardPendingEditsForKeys(pendingRowKeys)}
                      disabled={applying}
                    >
                      Discard all edits
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary text-sm"
                    onClick={() => {
                      setLockOverrideAcknowledged(false)
                      setShowApplyConfirm(true)
                    }}
                    disabled={applying || pendingChanges.length === 0}
                  >
                    {applying ? 'Applying…' : 'Apply changes'}
                  </button>
                  <TableToolbar
                    filterCount={activeResultFilterCount}
                    sortCount={sortSpecs.length}
                    showFilters={showResultFilters}
                    showSort={showSort}
                    showColumns={showColumns}
                    onToggleFilters={() => setShowResultFilters((open) => !open)}
                    onToggleSort={() => setShowSort((open) => !open)}
                    onToggleColumns={() => setShowColumns((open) => !open)}
                  />
                </div>
              }
              filterPills={
                <ActiveFilterPillBar
                  pills={resultFilterPills}
                  onRemovePill={(pillId) =>
                    setResultFilterTree(removeFilterPill(resultFilterTree, pillId))
                  }
                  onClearAll={() => clearResultFilters()}
                />
              }
            >
              <div className="table-scroll-mobile min-h-0 overflow-x-auto overflow-y-auto overscroll-x-contain">
              <table
                className="table-forecast border-separate border-spacing-0"
                style={tableStyle}
              >
                <thead>
                  <tr>
                    {visibleColumnKeys.map((columnKey) =>
                      renderTableHeaderCell(columnKey as QboTxnColumnKey)
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isConflictModeResults && displayBuckets
                    ? displayBuckets.map(({ groupId, rows: groupRows }) => {
                        const view = conflictGroupViewById.get(groupId)
                        const isCollapsed = collapsedGroupIds.has(groupId)
                        const allGroupSelected =
                          groupRows.length > 0 &&
                          groupRows.every((r) => selectedRowKeys.has(rowKey(r)))
                        const someGroupSelected = groupRows.some((r) =>
                          selectedRowKeys.has(rowKey(r))
                        )
                        return (
                          <Fragment key={groupId}>
                            <tr className="bg-gray-50">
                              <td colSpan={visibleColumnCount} className="!p-0">
                                <div
                                  className="sticky left-0 z-[5] w-max max-w-[min(100vw,56rem)] cursor-pointer bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900"
                                  onClick={() => toggleGroupCollapsed(groupId)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault()
                                      toggleGroupCollapsed(groupId)
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={!isCollapsed}
                                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} group ${view?.contactName ?? groupId}`}
                                >
                                  <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2">
                                    <div className="flex shrink-0 items-center gap-2">
                                      {isCollapsed ? (
                                        <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                      ) : (
                                        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                      )}
                                      <input
                                        type="checkbox"
                                        checked={allGroupSelected}
                                        ref={(el) => {
                                          if (el) {
                                            el.indeterminate =
                                              someGroupSelected && !allGroupSelected
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => toggleGroupSelection(groupRows)}
                                        className="inline-block origin-center scale-110 rounded border-gray-300 align-middle"
                                        aria-label={`Select all lines in group ${view?.contactName ?? groupId}`}
                                      />
                                    </div>
                                    <span className="min-w-0 truncate">
                                      {view?.contactName ?? '—'} —{' '}
                                      {view?.representativeDescription ?? '—'}
                                    </span>
                                    <div className="col-start-2 flex min-w-0 items-center gap-x-2 text-xs font-normal">
                                      {view?.categories.length ? (
                                        <span className="min-w-0 truncate text-gray-600">
                                          {view.categories
                                            .map(formatConflictCategoryChip)
                                            .join(' · ')}
                                        </span>
                                      ) : null}
                                      <span className="shrink-0 whitespace-nowrap text-gray-500">
                                        {formatConflictGroupLineCount(
                                          view?.visibleRowCount ?? groupRows.length,
                                          view?.totalRowCount ?? groupRows.length
                                        )}
                                      </span>
                                      <button
                                        type="button"
                                        className="shrink-0 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          openBulkEditForRows(groupRows)
                                        }}
                                      >
                                        Set category
                                      </button>
                                      {view ? (
                                        <button
                                          type="button"
                                          className="shrink-0 font-medium text-gray-600 hover:text-gray-800 hover:underline"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            void handleIgnoreGroup(view)
                                          }}
                                          disabled={ignoresSaving}
                                        >
                                          Ignore group
                                        </button>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            {!isCollapsed
                              ? (() => {
                                  const groupOrderedKeys = groupRows.map(rowKey)
                                  return groupRows.map((row, localIndex) =>
                                    renderTransactionRow(row, localIndex, groupOrderedKeys)
                                  )
                                })()
                              : null}
                          </Fragment>
                        )
                      })
                    : sortedData.map((row, index) =>
                        renderTransactionRow(row, index, sortedDataKeys)
                      )}
                </tbody>
              </table>
              </div>
            </TablePageShell>
          ) : null}

          {isConflictModeResults && rows.length > 0 && conflictViewTab === 'ignored' ? (
            <div className="card flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {ignoresLoading && ignoredConflictCount === 0 ? (
                  <p className="text-sm text-gray-500">Loading ignore settings…</p>
                ) : null}
                {ignoredGroups.length > 0 ? (
                  <section>
                    <h4 className="text-sm font-semibold text-gray-900">Ignored groups</h4>
                    <ul className="mt-2 divide-y divide-gray-100">
                      {ignoredGroups.map((group) => (
                        <li
                          key={group.group_id}
                          className="flex items-start justify-between gap-3 py-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {group.contact_name}
                            </p>
                            <p className="mt-0.5 text-sm text-gray-600">
                              {group.representative_description}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-ghost shrink-0 text-sm"
                            onClick={() => void handleUnignoreGroup(group.group_id)}
                            disabled={ignoresSaving}
                          >
                            Unignore
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {ignoredDescriptions.length > 0 ? (
                  <section className={ignoredGroups.length > 0 ? 'mt-6' : ''}>
                    <h4 className="text-sm font-semibold text-gray-900">Ignored descriptions</h4>
                    <ul className="mt-2 divide-y divide-gray-100">
                      {ignoredDescriptions.map((entry) => {
                        const entryKey = `${entry.contact_name}\0${entry.normalized_description}`
                        return (
                          <li
                            key={entryKey}
                            className="flex items-start justify-between gap-3 py-3"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {entry.contact_name}
                              </p>
                              <p className="mt-0.5 text-sm text-gray-600">
                                {entry.display_description}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="btn btn-ghost shrink-0 text-sm"
                              onClick={() => void handleUnignoreDescription(entry)}
                              disabled={ignoresSaving}
                            >
                              Unignore
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                ) : null}
                {!ignoresLoading && ignoredConflictCount === 0 ? (
                  <p className="text-sm text-gray-500">No ignored groups or descriptions.</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {showTabResults && rows.length > 0 ? (
        <FilterBuilderDrawer
          open={showResultFilters}
          onClose={() => setShowResultFilters(false)}
          fields={qboFilterFields}
          root={resultFilterTree}
          onChange={setResultFilterTree}
          onClearAll={() => setResultFilterTree(null)}
        />
      ) : null}

      <SortBuilder
        open={showSort}
        fields={QBO_TXN_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={handleSortSpecsChange}
        onClose={() => setShowSort(false)}
        onClearAll={() => {
          setHasUserSorted(false)
          setSortSpecs(QBO_TXN_DEFAULT_SORT)
        }}
      />

      <ColumnPicker
        open={showColumns}
        fields={QBO_TXN_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={handleColumnVisibilityChange}
        onClose={() => setShowColumns(false)}
        onReset={() => {
          handleColumnVisibilityChange(getDefaultColumnVisibility(QBO_TXN_FIELD_DEFINITIONS))
        }}
      />

      {showApplyConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Apply changes?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This will update <strong>{pendingChanges.length}</strong> line
              {pendingChanges.length !== 1 ? 's' : ''} with pending changes across{' '}
              <strong>{uniqueTxnCount}</strong> transaction{uniqueTxnCount !== 1 ? 's' : ''} in
              QuickBooks. Refinement filters do not limit which rows are applied.
            </p>

            {lockedPendingChanges.length > 0 ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                  <div>
                    <p className="font-medium">
                      {lockedPendingChanges.length} line
                      {lockedPendingChanges.length !== 1 ? 's' : ''} (
                      {lockedUniqueTxnCount} transaction{lockedUniqueTxnCount !== 1 ? 's' : ''}) fall
                      in a locked period
                    </p>
                    <p className="mt-1 text-amber-800">
                      These transactions are dated before your lock date (
                      <strong>{lockDate}</strong>), meaning that month or year has already been
                      closed. Applying will submit these changes to QuickBooks anyway.
                    </p>
                  </div>
                </div>
                <label className="mt-3 flex items-start gap-2 text-amber-900">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={lockOverrideAcknowledged}
                    onChange={(e) => setLockOverrideAcknowledged(e.target.checked)}
                  />
                  <span>
                    I understand these transactions are in a closed period and want to apply
                    changes anyway.
                  </span>
                </label>
              </div>
            ) : null}

            {pendingHeaderLocationChanges.length > 0 ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                  <div>
                    <p className="font-medium">
                      Location changes apply to the whole Bill or Expense
                    </p>
                    <p className="mt-1 text-amber-800">
                      {pendingHeaderLocationChanges.length} line
                      {pendingHeaderLocationChanges.length !== 1 ? 's' : ''} across{' '}
                      {pendingHeaderLocationTxnCount} Bill
                      {pendingHeaderLocationTxnCount !== 1 ? 's' : ''}/Expense
                      {pendingHeaderLocationTxnCount !== 1 ? 's' : ''} include a new location.
                      QuickBooks stores location on the transaction header, so the updated location
                      will apply to <strong>all line items</strong> on each affected Bill or
                      Expense.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {pendingHeaderDocNumberChanges.length > 0 ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                  <div>
                    <p className="font-medium">
                      Journal # changes apply to the whole transaction
                    </p>
                    <p className="mt-1 text-amber-800">
                      {pendingHeaderDocNumberChanges.length} line
                      {pendingHeaderDocNumberChanges.length !== 1 ? 's' : ''} across{' '}
                      {pendingHeaderDocNumberTxnCount} transaction
                      {pendingHeaderDocNumberTxnCount !== 1 ? 's' : ''} include a new journal
                      number. QuickBooks stores the journal number on the transaction header, so the
                      updated journal number will apply to <strong>all line items</strong> on each
                      affected transaction.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={() => setShowApplyConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary text-sm"
                disabled={lockedPendingChanges.length > 0 && !lockOverrideAcknowledged}
                onClick={() => void handleApply()}
              >
                {lockedPendingChanges.length > 0 ? 'Apply anyway' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkEditOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-txn-edit-title"
        >
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5 sm:max-h-[min(85vh,720px)] sm:rounded-2xl">
            <div className="flex-shrink-0 border-b border-gray-100 px-5 pb-4 pt-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3
                      id="bulk-txn-edit-title"
                      className="text-lg font-semibold tracking-tight text-gray-900"
                    >
                      Bulk edit lines
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                      {selectedRowKeys.size} line{selectedRowKeys.size === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-snug text-gray-500">
                    {isConflictModeResults
                      ? 'Category is on by default — choose the category to apply to all selected lines.'
                      : 'Turn on the fields you want to overwrite. Everything else stays as-is on each selected line.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setBulkEditOpen(false)}
                  className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-3">
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkApplyCategory
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-txn-category"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkApplyCategory}
                      onChange={(e) => setBulkApplyCategory(e.target.checked)}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-txn-category"
                          className="cursor-pointer text-sm font-semibold text-gray-900"
                        >
                          Category
                        </label>
                        {bulkApplyCategory ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        ) : null}
                      </div>
                      <SearchableSelect
                        options={tableCategoryOptions}
                        value={bulkCategoryNew}
                        onChange={setBulkCategoryNew}
                        placeholder="Choose category…"
                        disabled={!bulkApplyCategory}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkApplyDescription
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-txn-description"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkApplyDescription}
                      onChange={(e) => setBulkApplyDescription(e.target.checked)}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-txn-description"
                          className="cursor-pointer text-sm font-semibold text-gray-900"
                        >
                          Description
                        </label>
                        {bulkApplyDescription ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        ) : null}
                      </div>
                      <input
                        id="bulk-txn-description-value"
                        className="input w-full"
                        value={bulkDescriptionNew}
                        onChange={(e) => setBulkDescriptionNew(e.target.value)}
                        disabled={!bulkApplyDescription}
                        placeholder="Line description"
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkApplyLocation
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-txn-location"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkApplyLocation}
                      onChange={(e) => setBulkApplyLocation(e.target.checked)}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-txn-location"
                          className="cursor-pointer text-sm font-semibold text-gray-900"
                        >
                          Location
                        </label>
                        {bulkApplyLocation ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        ) : null}
                      </div>
                      <SearchableSelect
                        options={tableLocationOptions}
                        value={bulkLocationNew}
                        onChange={(id) => setBulkLocationNew(id)}
                        placeholder="Choose location…"
                        disabled={!bulkApplyLocation}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={() => setBulkEditOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary text-sm"
                onClick={handleBulkEditApply}
              >
                Apply to selected
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AccountingToolPage>
  )
}
